import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import GuildCard from "../components/GuildCard";
import Icon from "../components/Icon";
import { guilds, guildFilters } from "../data";
import { matchesQuery } from "../utils/format";

const defaultFilters = {
  search: "",
  category: "all"
};

const Directory = () => {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") ?? "";
  const [filters, setFilters] = useState({ ...defaultFilters, search: initialSearch });

  useEffect(() => {
    setFilters((prev) => ({ ...prev, search: initialSearch }));
  }, [initialSearch]);

  const filteredGuilds = useMemo(() => {
    return guilds.filter((guild) => {
      if (filters.search && !matchesQuery(`${guild.name} ${guild.description}`, filters.search)) return false;
      if (filters.category !== "all" && guild.category !== filters.category) return false;
      return true;
    });
  }, [filters]);

  const updateFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const clearFilters = () => setFilters(defaultFilters);
  const activeFilterEntries = Object.entries(filters).filter(([key, value]) => key !== "search" && value !== "all");

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <p className="eyebrow">Directory</p>
        <h1 className="text-4xl text-slate-100">Browse guilds with calm, deliberate filters</h1>
        <p className="text-slate-300 max-w-3xl">
          Each listing includes platform coverage, map access, and a concise narrative of the ecosystem. Use the search bar or category filter to focus on the workstreams most relevant to your team.
        </p>
        <div className="grid gap-4 text-sm text-slate-300 sm:grid-cols-3">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <span className="icon-badge icon-badge--primary">
                <Icon name="network" />
              </span>
              <div>
                <p className="stat-card__value">{filteredGuilds.length}</p>
                <span className="text-xs uppercase tracking-[0.3em] text-slate-500">Guilds visible</span>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <span className="icon-badge icon-badge--sun">
                <Icon name="layers" />
              </span>
              <div>
                <p className="stat-card__value">{guildFilters.categories.length}</p>
                <span className="text-xs uppercase tracking-[0.3em] text-slate-500">Categories</span>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <span className="icon-badge icon-badge--coral">
                <Icon name="map" />
              </span>
              <div>
                <p className="stat-card__value">{guildFilters.platforms.length}</p>
                <span className="text-xs uppercase tracking-[0.3em] text-slate-500">Platforms monitored</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="panel space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.3em] text-slate-500">Search</label>
              <input
                value={filters.search}
                onChange={(event) => updateFilter("search", event.target.value)}
                placeholder="Search by guild or keywords"
                className="w-full rounded-2xl border border-brand-ink/60 bg-brand-night/80 px-4 py-3 text-sm text-slate-100 placeholder:text-brand-steel focus:border-brand-primary"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.3em] text-slate-500">Category</label>
              <select
                value={filters.category}
                onChange={(event) => updateFilter("category", event.target.value)}
                className="w-full rounded-2xl border border-brand-ink/60 bg-brand-night/80 px-4 py-3 text-sm text-slate-100"
              >
                <option value="all">All categories</option>
                {guildFilters.categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
            <p>
              Showing <span className="text-slate-100">{filteredGuilds.length}</span> guilds
            </p>
            {activeFilterEntries.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {activeFilterEntries.map(([key, value]) => (
                  <span key={key} className="chip chip-soft capitalize">
                    {key}: {value}
                  </span>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={clearFilters}
              className="text-slate-400 underline-offset-4 hover:text-slate-100 hover:underline"
            >
              Reset filters
            </button>
          </div>
      </section>

      <section className="grid gap-5 md:grid-cols-2">
          {filteredGuilds.map((guild) => (
            <GuildCard key={guild.id} guild={guild} />
          ))}
          {filteredGuilds.length === 0 && (
            <p className="text-slate-400">No guilds match those filters yet.</p>
          )}
      </section>
    </div>
  );
};

export default Directory;
